import { useNavigate } from "react-router-dom";
import { MainRoutes } from "../../../shared/models";
import { Id } from "../../../convex/_generated/dataModel";
import { useStableQuery } from "../../../shared/hooks/useStableQuery";
import { api } from "../../../convex/_generated/api";
import { useContext, useMemo } from "react";
import { BaseContext } from "../../../shared/base-context";
import { TrophyIcon } from "@heroicons/react/24/outline";
import { ChatAboutButton } from "./ChatAboutButton";

export type TraitBadgeProps = {
    trait: string;
    id: Id<"skills">;
    shortDescription: string;
}

export const TraitBadge = ({ trait, id, shortDescription }: TraitBadgeProps) => {
    const navigate = useNavigate();
    const { convexUserId } = useContext(BaseContext);
    const userXpData = useStableQuery(api.user_xp.getUserXpWithLevelInfo, convexUserId ? { user_id: convexUserId as Id<"users"> } : "skip");
    const completedSessions = useStableQuery(
        api.completed_sessions.listBySkillAndUser,
        convexUserId ? { skill_id: id, user_id: convexUserId as Id<"users">, limit: 1 } : "skip"
    );
    const hasCompletedSessions = completedSessions && completedSessions.length > 0;

    // Get skill XP data for this specific skill
    const skillXpData = useMemo(() => {
        if (!userXpData?.skill_xp) return null;
        return userXpData.skill_xp.find(s => s.skill_id === id);
    }, [userXpData, id]);

    const openTraitPage = (traitId: Id<"skills">) => {
        navigate(`/skills/${traitId}`);
    }

    // Calculate next level threshold
    const currentXp = skillXpData?.xp || 0;
    const xpToNextLevel = skillXpData?.xp_to_next_level || 0;
    const nextLevelXp = currentXp + xpToNextLevel;
    const currentLevel = skillXpData?.level || 1;
    const progressPercent = nextLevelXp > 0 ? Math.round((currentXp / nextLevelXp) * 100) : 0;
    const isMaxLevel = skillXpData?.is_max_level || false;

    return (
        <div
            onClick={() => openTraitPage(id)}
            className="group cursor-pointer w-full border-2 border-border/50 bg-card hover:border-primary/40 hover:shadow-md transition-all rounded-xl"
        >
            <div className="p-4 sm:p-6">
                {/* Header with skill name and level badge */}
                <div className="flex items-center justify-between gap-3 mb-4">
                    <h4 className="text-lg sm:text-xl text-foreground group-hover:text-primary transition-colors min-w-0">
                        {trait}
                    </h4>
                    <div className="flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 py-1.5 sm:py-2 rounded-full bg-primary/10 border-2 border-primary/30 shrink-0">
                        <TrophyIcon className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                        <span className="text-sm sm:text-lg font-semibold text-primary whitespace-nowrap">
                            Level {currentLevel}
                        </span>
                    </div>
                </div>

                {/* Points display */}
                <div className="flex items-baseline gap-2">
                    <span className="text-xl sm:text-2xl font-semibold text-foreground">{currentXp}</span>
                    {!isMaxLevel && (
                        <span className="text-sm text-muted-foreground">/ {nextLevelXp} pts</span>
                    )}
                    {isMaxLevel && (
                        <span className="text-sm text-muted-foreground">pts (Max Level)</span>
                    )}
                </div>

                {/* Progress bar */}
                <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                    <div
                        className="h-full bg-primary transition-all duration-500"
                        style={{ width: `${progressPercent}%` }}
                    />
                </div>

                {/* Points until next level */}
                {!isMaxLevel && (
                    <p className="text-xs text-muted-foreground mt-2">
                        {xpToNextLevel} pts until Level {currentLevel + 1}
                    </p>
                )}
                {isMaxLevel && (
                    <p className="text-xs text-muted-foreground mt-2">
                        Max level reached!
                    </p>
                )}

                {/* Chat about this skill button — only show if user has completed sessions for this skill */}
                {hasCompletedSessions && (
                    <div className="mt-4 pt-3 border-t border-border/50">
                        <ChatAboutButton
                            contextType="skill"
                            contextId={`skill_${id}`}
                            systemMessage={`User tapped Discuss on their "${trait}" skill (Level ${currentLevel}, ${currentXp} XP${!isMaxLevel ? `, ${xpToNextLevel} XP to next level` : ', Max Level'}). Call get_skill_progress for this skill, then respond in 1-2 sentences. Keep it brief.`}
                            autoSubmitPrompt={`I'd like to talk about my ${trait} skill progress.`}
                            metadata={{ skill_id: id }}
                            label="Chat about this skill"
                            variant="text"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
